import { DateTimefromISO } from '#lib/helper/date.js';
import { isGiftcardRecentlyMutated } from '#lib/helper/giftcard.js';
import { getNotificationLink } from '#lib/helper/notification.js';
import { isSoundAllowed, playSound } from '#lib/helper/sound.js';
import { t } from '#lib/stores/i18n.js';
import { downloadFileFromUrl } from '#lib/helper/browser.js';
import { unreadReviewsCountStore } from '#lib/stores/review.js';
import { loadSmsRefills } from '#lib/stores/smsRefills.js';
import { loadSmsSettings } from '#lib/stores/smsSettings.js';
import { toastStore } from '#lib/stores/toast.js';
import { AppEvent } from '#lib/types/generated/index.js';
import { hydrateNotification } from '#lib/types/generated/index.js';
import {
	apiAppGiftcardGiftcardGetgiftcards,
	apiAppGiftcardGiftcardGetgiftcardstats
} from '#lib/types/generated/api.gen.js';
import { DateTime } from 'luxon';
import { get } from 'svelte/store';
import { convertApiScanToScan, scansHistory } from '../qr-code/qr-code';
import { loadContract, refreshPaymentMethod } from './billing';
import { loadBlockingPeriods } from './blocking-period';
import { loadBooking } from './booking';
import { doNotDisturbStore } from './doNotDisturb';
import { reloadFeatureFlip } from './feature-flip';
import { giftcardStatsStore, giftcardStore, upsertGiftcard } from './giftcard';
import { markListAsNotFreshUnlessExists } from './infinity-list';
import { loadMemo, memoStore } from './memo';
import {
	markNotificationAsRead,
	notificationStatsStore,
	notificationStore,
	refreshNotificationStats,
	updateNotificationReadState
} from './notification';
import { getNotificationSound } from './notification-setting';
import { reloadOpeningHours } from './opening-hour';
import { loadExceptions } from './opening-hour-exception';
import { upsertCustomerPackage } from './package';
import { hydrateCustomerPackage } from '#lib/types/generated/index.js';
import { loadSessionInstance } from './session-instance';
import { onSocketEvent } from './socket';

let loaded = false;

export function initSocketWatchers() {
	if (loaded) return;
	loaded = true;

	// Opening hours & exceptions
	onSocketEvent(
		[
			AppEvent.OPENING_HOUR_UPDATED,
			AppEvent.OPENING_HOUR_CREATED,
			AppEvent.OPENING_HOUR_DELETED
		],
		() => {
			reloadOpeningHours();
		}
	);

	onSocketEvent(
		[
			AppEvent.OPENING_HOUR_DELETED,
			AppEvent.OPENING_HOUR_EXCEPTION_CREATED,
			AppEvent.OPENING_HOUR_EXCEPTION_UPDATED,
			AppEvent.OPENING_HOUR_EXCEPTION_DELETED
		],
		() => {
			loadExceptions();
		}
	);

	// Memos
	onSocketEvent([AppEvent.MEMO_CREATED, AppEvent.MEMO_UPDATED], (data) => {
		loadMemo(data.memo_id);
	});

	onSocketEvent(AppEvent.MEMO_DELETED, (data) => {
		memoStore.update((memos) => memos.filter((m) => m.id !== data.memo_id));
	});

	// Contract & Billing
	onSocketEvent(
		[
			AppEvent.BILLING_INVOICE_PAID,
			AppEvent.BILLING_INVOICE_UNPAID,
			AppEvent.BILLING_PLAN_SANCTION_REACTIVATION,
			AppEvent.BILLING_PLAN_SANCTION_WARNING,
			AppEvent.BILLING_PLAN_SANCTION_SUSPENSION
		],
		() => {
			loadContract(true);
		}
	);

	onSocketEvent(AppEvent.PAYMENT_METHOD_UPDATED, () => {
		refreshPaymentMethod();
	});

	// Do Not Disturb
	onSocketEvent(AppEvent.USER_DO_NOT_DISTURB_UPDATED, (data) => {
		if (data.until) {
			doNotDisturbStore.set({
				enabled: true,
				end: DateTimefromISO(data.until)
			});
		} else {
			doNotDisturbStore.set({
				enabled: false,
				end: null
			});
		}
	});

	// Giftcards
	let statsReloadTimeout: ReturnType<typeof setTimeout> | undefined;
	function reloadGiftcardStatsDebounced() {
		if (statsReloadTimeout) clearTimeout(statsReloadTimeout);
		statsReloadTimeout = setTimeout(() => {
			apiAppGiftcardGiftcardGetgiftcardstats().then((stats) => {
				giftcardStatsStore.set(stats);
			});
		}, 300);
	}

	function reloadGiftcardFromApi(giftcardId: string) {
		apiAppGiftcardGiftcardGetgiftcards({ id: giftcardId }).then((gcs) => {
			const items = gcs.items;
			if (items.length === 0) return;
			upsertGiftcard(items[0]);
		});
	}

	onSocketEvent(AppEvent.GIFTCARD_CONSUMED, (data) => {
		if (isGiftcardRecentlyMutated(data.giftcard_id)) {
			return; // Locally mutated by current client: optimistic update already applied, skip redundant fetch
		}
		reloadGiftcardFromApi(data.giftcard_id);
		reloadGiftcardStatsDebounced();
	});

	onSocketEvent(AppEvent.GIFTCARD_RECREDITED, (data) => {
		if (isGiftcardRecentlyMutated(data.giftcard_id)) {
			return; // Locally mutated by current client: optimistic update already applied, skip redundant fetch
		}
		reloadGiftcardFromApi(data.giftcard_id);
		reloadGiftcardStatsDebounced();
	});

	onSocketEvent(
		[
			AppEvent.GIFTCARD_CREATED,
			AppEvent.GIFTCARD_ORDER_PAYED,
			AppEvent.BOOKABLE_GIFTCARD_CREATED,
			AppEvent.BOOKABLE_GIFTCARD_UPDATED,
			AppEvent.BOOKABLE_GIFTCARD_DELETED
		],
		(data) => {
			if ('giftcard_id' in data && data.giftcard_id) {
				apiAppGiftcardGiftcardGetgiftcards({
					id: data.giftcard_id
				}).then((gcs) => {
					const items = gcs.items;
					if (items.length === 0) {
						return;
					}
					upsertGiftcard(items[0]);
				});
			}
		}
	);

	onSocketEvent(AppEvent.GIFTCARD_DELETED, (data) => {
		giftcardStore.set(get(giftcardStore).filter((g) => g.id !== data.giftcard_id));
	});

	// Blocking Periods
	onSocketEvent(AppEvent.BLOCKING_PERIODS_REFRESHED, () => {
		loadBlockingPeriods();
	});

	// Notifications
	const knownNotificationsIds: number[] = [];

	onSocketEvent(AppEvent.NOTIFICATION_CREATED, (data) => {
		const notificationId = data.notification_id;
		const notification = hydrateNotification(data.notification);

		// Do nothing if the notification is already known by our store (duplicate socket message)
		if (knownNotificationsIds.includes(notificationId)) {
			return;
		}

		knownNotificationsIds.push(notificationId);
		markListAsNotFreshUnlessExists(notificationId, 'id', notificationStore);

		if (isSoundAllowed()) {
			const sound = getNotificationSound(notification.event);
			if (sound) {
				playSound(sound);
			}
		}

		notificationStatsStore.update((value) => ({
			unread: value.unread + 1,
			knownNotificationsIds: [...value.knownNotificationsIds, notificationId]
		}));

		if (notification.event === AppEvent.EXPORT_READY) {
			const link = getNotificationLink(notification);
			const content = get(t)('notification.export_ready.title');
			toastStore.update((toasts) => [
				...toasts,
				{
					text: content,
					icon: 'export',
					bgColor: 'success',
					date: DateTime.now().toMillis() + Math.random(),
					goto: () => {
						if (!notification.isRead) {
							markNotificationAsRead(notification);
						}
						if (link) {
							const filename =
								'exportTitle' in notification.data &&
								typeof notification.data.exportTitle === 'string'
									? `${notification.data.exportTitle}.csv`
									: undefined;
							downloadFileFromUrl(link, filename);
						}
					}
				}
			]);
		}
	});

	onSocketEvent(AppEvent.NOTIFICATION_ALL_READ, () => {
		notificationStore.update((store) => {
			store.data.forEach((notification) => {
				notification.isRead = true;
			});
			return store;
		});

		notificationStatsStore.update((value) => ({
			unread: 0,
			knownNotificationsIds: value.knownNotificationsIds
		}));
	});

	onSocketEvent(AppEvent.NOTIFICATION_READ, (data) => {
		updateNotificationReadState(data.notification_id, true);
		refreshNotificationStats();
	});

	onSocketEvent(AppEvent.NOTIFICATION_UNREAD, (data) => {
		updateNotificationReadState(data.notification_id, false);
		refreshNotificationStats();
	});

	onSocketEvent(AppEvent.BOOKING_REVIEW_UNREAD_COUNT_UPDATED, (data) => {
		unreadReviewsCountStore.update(() => data.count);
	});

	// Bookings
	onSocketEvent(
		[
			AppEvent.BOOKING_CREATED,
			AppEvent.BOOKING_UPDATED,
			AppEvent.BOOKING_INTENT,
			AppEvent.BOOKING_INTENT_EXPIRED,
			AppEvent.BOOKING_BOOKED,
			AppEvent.BOOKING_TO_BE_PAYED,
			AppEvent.BOOKING_CONFIRMED,
			AppEvent.BOOKING_TO_BE_CONFIRMED,
			AppEvent.BOOKING_TO_BE_RECONFIRMED,
			AppEvent.BOOKING_RECONFIRMED,
			AppEvent.BOOKING_LEFT,
			AppEvent.BOOKING_CANCELED,
			AppEvent.BOOKING_NO_SHOW,
			AppEvent.BOOKING_EXPIRED,
			AppEvent.BOOKING_PAYMENT_TRANSACTION_REFUNDED
		],
		(data) => {
			if (data.booking_id) {
				loadBooking(data.booking_id, (b) => {
					loadSessionInstance(b.date);
				});
			}
		}
	);

	// QR Code
	onSocketEvent(AppEvent.QR_CODE_SCANNED, (data) => {
		const s = convertApiScanToScan(data);
		scansHistory.update((history) => [...history.filter((scan) => scan.code !== s.code), s]);
	});

	// SMS
	onSocketEvent(
		[
			AppEvent.BUSINESS_SMS_CREDITS_REFILL_REQUEST_AUTO,
			AppEvent.BUSINESS_SMS_CREDITS_REFILL_REQUEST_MANUAL
		],
		() => {
			loadSmsRefills();
		}
	);

	onSocketEvent(AppEvent.BUSINESS_SMS_CREDITS_UPDATED, () => {
		loadSmsSettings();
		loadSmsRefills();
	});

	// Customer Package
	onSocketEvent(
		[AppEvent.CUSTOMER_PACKAGE_CREATED, AppEvent.CUSTOMER_PACKAGE_UPDATED],
		(data) => {
			upsertCustomerPackage(hydrateCustomerPackage(data.customer_package));
		}
	);

	// Feature Flipping
	onSocketEvent(AppEvent.BUSINESS_FEATURE_FLIPPING_UPDATED, () => {
		reloadFeatureFlip();
	});
}
